import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ConfirmationService, MessageService, SortMeta, TreeNode} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {InfoView, Query} from '../filter.type';
import {FilterPresetService} from './filter-preset.service';
import {EngineTagEventService, HavingCountTable, SaveQuery, TagEventDTO} from '@tj/tj-dmp-lib-api';
import {OverlayPanel} from 'primeng/overlaypanel';
import {FilterBuilderService} from '../../../services/filter-builder.service';
import {TableMetaComponent} from '../../table-data/table-meta/table-meta.component';
import {AccountService} from '../../../auth/account.service';
import {SelectAffectedPartnersComponent} from '../../select-affected-partners/select-affected-partners.component';
import {cronLabel} from '../../input-cron/cron-label';

interface DefaultSave {
    name: string;
}

export interface ClassicTreeSave {
    id: string;
    name: string;
    value: TreeNode;
    cols: string[];
    orderings: SortMeta[];
    having: HavingCountTable[];
    partner: string;
    affectedPartners: string[];
    isModel: boolean;
}

type Save = DefaultSave | ClassicTreeSave;


@Component({
    selector: 'dmp-filter-preset',
    templateUrl: './filter-preset.component.html',
    styleUrls: ['./filter-preset.component.scss'],
    providers: [MessageService],
    standalone: false
})
export class FilterPresetComponent implements OnInit {

    constructor(private service: FilterPresetService,
                private confirmationService: ConfirmationService,
                protected messageService: MessageService,
                private filterBuilderService: FilterBuilderService,
                private accountService: AccountService,
                private cdr: ChangeDetectorRef,
                private engineTagEventService: EngineTagEventService,
                private router: Router,
                private readonly translate: TranslateService) {
        this.defaultName = this.translate.instant('components.filter-preset.newPreset');
    }

    // Calculé une fois ici : un changement de langue recharge la page, donc la traduction reste
    // valide pendant toute la vie du composant.
    private readonly defaultName: string;

    @Input() set currentView(info: InfoView) {
        const hash = JSON.stringify(info);
        if (hash !== this._hash) {
            this._currentView = info;
            this._hash = JSON.stringify(info);
            this.service.loadAll(this._currentView);
            this.refreshTagRules();
        }
    }
    saves: ClassicTreeSave[] = [];
    currentSave: Save;
    createDialog = false;
    // Lié par [(ngModel)] dans les modales d'enregistrement ; le champ n'était pas déclaré et
    // n'existait donc qu'à l'exécution, créé par Angular sur l'instance.
    name: string = null;
    settingDialog = false;
    saveConfirmDialog = false;
    copyToTenantDialog = false;
    selectedAffectedPartners: string[] = [];
    hasPartner = false;

    isSuperAdmin: boolean;
    saveName: string;

    @Input() tenant: string;
    @Input() current: TreeNode;
    @Input() currentQuery: Query;
    @Output() onLoad = new EventEmitter<ClassicTreeSave>();
    @Output() onSave = new EventEmitter<ClassicTreeSave>();
    /** Le préset actif a été retiré (croix du sélecteur) : au parent de retirer ses filtres. */
    @Output() onUnload = new EventEmitter<void>();
    original: TreeNode;
    _hash: string;

    public modeEdit: 'CLONE' | 'CREATE';
    saveConfirmDialogContext: 'MODEL_WITH_VALUES' | 'MODEL_STRUCTURE_CHANGED' | 'PRESET_CHANGED' = null;

    @ViewChild('affectedPartnersSettings') affectedPartnersSettings: SelectAffectedPartnersComponent;
    @ViewChild('tagRulesPanel') tagRulesPanel: OverlayPanel;

    /** Règles de tag du preset chargé (vue courante). */
    tagRules: TagEventDTO[] = [];
    /**
     * Requête de l'écran juste après le chargement ou l'enregistrement du preset. Une règle applique les
     * filtres enregistrés : si l'écran en diffère (valeur changée, recherche globale…), on la refuse.
     * `filtersChanged()` ne suffit pas : conçue pour les modèles, elle ignore les valeurs.
     */
    private presetQuerySnapshot: string = null;

    _currentView: InfoView;
    @Input() table!: TableMetaComponent;
    public saveList: { label: string; items: Save[] }[];
    @Input() tabParam!: ClassicTreeSave;

    private buildSaveList(): void {
        const models = this.saves.filter(s => s.isModel);
        const presets = this.saves.filter(s => !s.isModel);
        const defaultItem = [{name: this.defaultName}];

        if (models.length === 0 && presets.length === 0) {
            this.saveList = [
                {label: this.translate.instant('components.filter-preset.createPreset'), items: defaultItem}
            ];
        } else if (presets.length === 0) {
            this.saveList = [
                {label: this.translate.instant('components.filter-preset.createPreset'), items: defaultItem},
                {label: this.translate.instant('components.filter-preset.models'), items: models}
            ];
        } else if (models.length === 0) {
            this.saveList = [
                {label: this.translate.instant('components.filter-preset.createPreset'), items: defaultItem},
                {label: this.translate.instant('components.filter-preset.presets'), items: presets}
            ];
        } else {
            this.saveList = [
                {label: this.translate.instant('components.filter-preset.createPreset'), items: defaultItem},
                {label: this.translate.instant('components.filter-preset.models'), items: models},
                {label: this.translate.instant('components.filter-preset.presets'), items: presets}
            ];
        }
    }


    ngOnInit(): void {
        this.currentSave = {name: this.defaultName} as Save;
        this.service.list$.subscribe((el) => {
            this.saves = el.map(item =>
                this.filterBuilderService.buildClassicTreeSaveBySaveQuery(item));
            this.buildSaveList();
        });
        this.service.currentSaveQuery$.subscribe((query: SaveQuery) => {
            setTimeout(() => {
                if (query && this.saves.length > 0) {
                    this.currentSave = this.saves.find(f => f.id === query.id);
                }
                this.refreshTagRules();
            });
        });
        this.accountService.fetch().subscribe((account) => {
            this.isSuperAdmin = account.admin;
        });
        this.service.loadAll(this._currentView);
    }

    load() {
        // La croix du sélecteur passe le modèle à null et émet onChange avant onClear : le retrait
        // du préset est traité par unload().
        if (!this.currentSave) {
            return;
        }
        const ctsCurrentSave = this.currentSave as ClassicTreeSave;
        const isDefault = ctsCurrentSave.name === this.defaultName;
        if (isDefault) {
            this.selectedAffectedPartners = [];
        } else {
            if (ctsCurrentSave.affectedPartners) {
                this.selectedAffectedPartners = ctsCurrentSave.affectedPartners;
            } else {
                this.selectedAffectedPartners = [];
            }
        }
        this.hasPartner = ctsCurrentSave.partner !== null && ctsCurrentSave.partner !== undefined && ctsCurrentSave.partner.trim() !== '';
        if (isDefault) {
            // "[Nouveau Preset]" n'est qu'un placeholder pour créer un préset : il n'a pas de "value".
            // Ne pas émettre onLoad ici, sinon le parent (doSaveQueryChanged -> doSaveQueryUpdated)
            // retombe sur son TreeNode vide par défaut et écrase le filtre en cours de construction/validation.
            this.cdr.markForCheck();
            return;
        }
        const copy = JSON.parse(JSON.stringify(ctsCurrentSave)) as ClassicTreeSave;
        this.onLoad.next(copy);
        this.service.load(copy.id);
        this.cdr.markForCheck();
        setTimeout(() => {
            if (this.current) {
                this.original = this.service.cloneTreeNode(this.current);
            }
            this.presetQuerySnapshot = this.querySnapshot();
            this.refreshTagRules();
            this.cdr.detectChanges();
        });
    }

    public save() {
        this.saveConfirmDialog = false;
        this.saveConfirmDialogContext = null;
        const save = this.currentSave as ClassicTreeSave;
        const cols = this.table.colsSelected.map(c => c.key);
        if (this.isSuperAdmin && !save.partner) {
            this.service.next({
                ...this.currentQuery,
                partner: '',
                affectedPartners: this.selectedAffectedPartners,
                isModel: save.isModel,
                cols,
                json: this.service.removeOneFilterWithNoType(this.current)
            });
        } else if (this.isSuperAdmin && save.partner) {
            this.service.next({
                ...this.currentQuery,
                partner: save.partner,
                affectedPartners: this.selectedAffectedPartners,
                isModel: save.isModel,
                cols,
                json: this.service.removeOneFilterWithNoType(this.current)
            });
        } else {
            this.service.next({
                ...this.currentQuery,
                partner: this._currentView.partner,
                affectedPartners: this.selectedAffectedPartners,
                isModel: save.isModel,
                cols,
                json: this.service.removeOneFilterWithNoType(this.current)
            });
        }
        this.service.update(this.tenant, this.currentSave.name);
        this.presetQuerySnapshot = this.querySnapshot();
        if (save.isModel) {
            this.messageService.add({severity: 'success', summary: this.translate.instant('components.filter-preset.modelUpdated')});
        } else {
            this.messageService.add({severity: 'success', summary: this.translate.instant('components.filter-preset.presetUpdated')});
        }
        this.onSave.emit(this.currentSave as ClassicTreeSave);
    }

    /**
     * Annulation des modales d'enregistrement. Branchée à la fois sur le bouton « Annuler » et sur
     * `(onHide)` du p-dialog : la croix en haut à droite doit remettre le formulaire dans le même
     * état que le bouton, sinon le nom saisi resterait en place à la réouverture.
     */
    cancelCreate() {
        this.createDialog = false;
        this.name = null;
    }

    cancelSaveConfirm() {
        this.saveConfirmDialog = false;
        this.name = null;
    }

    create(name: string, isModel: boolean) {
        if (this.presetExists(name)) {
            this.messageService.add({severity: 'error', summary: this.translate.instant('components.filter-preset.nameAlreadyExists')});
            return;
        } else {
            this.createDialog = false;
            this.saveConfirmDialog = false;
            this.saveConfirmDialogContext = null;
            this.selectedAffectedPartners = [];
            if (isModel) {
                this.service.cleanOneValueFilter(this.current);
            }
            const cols = this.table.colsSelected.map(c => c.key);
            this.service.next({
                ...this.currentQuery,
                partner: this._currentView.partner,
                isModel,
                cols,
                json: this.service.removeOneFilterWithNoType(this.current)
            });
            this.service.create(name);
            this.presetQuerySnapshot = this.querySnapshot();
            if (isModel) {
                this.messageService.add({severity: 'success', summary: this.translate.instant('components.filter-preset.modelCreated')});
            } else {
                this.messageService.add({severity: 'success', summary: this.translate.instant('components.filter-preset.presetCreated')});
            }
            this.onSave.emit(this.currentSave as ClassicTreeSave);
        }
    }

    public remove() {
        // Le configurateur détache les règles du preset à sa suppression : on le dit avant.
        const rules = this.tagRules.length;
        const message = rules === 0
            ? this.translate.instant('components.filter-preset.confirmDelete')
            : this.translate.instant(`components.filter-preset.confirmDeleteWithTagRules.${rules === 1 ? 'one' : 'other'}`,
                {count: rules});
        this.confirmationService.confirm({
            message,
            accept: () => {
                this.service.remove(this.tenant, (this.currentSave as ClassicTreeSave).id);
                this.tagRules = [];
                this.messageService.add({severity: 'success', summary: this.translate.instant('components.filter-preset.presetDeleted')});
            }
        });
    }

    public clone() {
        this.modeEdit = 'CLONE';
        this.createDialog = true;
    }

    public updateAffectedPartners($event) {
        this.selectedAffectedPartners = $event;
        const cols = this.table.colsSelected.map(c => c.key);
        this.service.next({
            ...this.currentQuery,
            affectedPartners: this.selectedAffectedPartners,
            isModel: (this.currentSave as ClassicTreeSave).isModel,
            cols,
            json: this.service.removeOneFilterWithNoType(this.current)
        });
        this.service.updatePartners(this.tenant, this.selectedAffectedPartners);
        this.messageService.add({ severity: 'success', summary: this.translate.instant('components.filter-preset.affectedPartnersUpdated') });
    }

    presetExists(name: string): boolean {
        return this.saves.some(s => s.name.trim().toLowerCase() === name.trim().toLowerCase());
    }

    filtersChanged(): boolean {
        return !this.service.compareOneOldNewTreeNode(this.current, this.original);
    }

    hasValues(): boolean {
        return this.service.hasValueInOneFilters(this.current);
    }

    /** Un vrai préset (ou modèle) est chargé, pas le placeholder « [Nouveau Preset] ». */
    hasActivePreset(): boolean {
        return !!this.currentSave && this.currentSave.name !== this.defaultName;
    }

    /**
     * Croix du sélecteur : plus aucun préset actif. Le sélecteur revient sur « [Nouveau Preset] »,
     * comme à l'ouverture de la page, et le parent retire les filtres du préset (`onUnload`).
     * Pas de confirmation : les filtres enregistrés restent dans le préset, il suffit de le
     * resélectionner pour les retrouver.
     */
    unload() {
        this.currentSave = {name: this.defaultName} as Save;
        this.tagRules = [];
        this.presetQuerySnapshot = null;
        this.selectedAffectedPartners = [];
        this.hasPartner = false;
        this.original = null;
        this.service.unload();
        this.onUnload.emit();
        this.cdr.markForCheck();
    }

    public saveContext() {
        if (this.currentSave.name === this.defaultName) {
            this.modeEdit = 'CREATE';
            this.createDialog = true;
        } else {
            const save = this.currentSave as ClassicTreeSave;
            const filtersChanged = this.filtersChanged();
            const hasValues = this.hasValues();
            if (save.isModel) {
                if (!filtersChanged && hasValues) {
                    this.openSaveConfirmDialog('MODEL_WITH_VALUES');
                } else if (filtersChanged) {
                    this.openSaveConfirmDialog('MODEL_STRUCTURE_CHANGED');
                } else {
                    this.save();
                }
            } else {
                this.openSaveConfirmDialog('PRESET_CHANGED');
            }
        }
        this.original = this.service.cloneTreeNode(this.current);
    }

    openSaveConfirmDialog(context: 'MODEL_WITH_VALUES' | 'MODEL_STRUCTURE_CHANGED' | 'PRESET_CHANGED') {
        this.saveConfirmDialogContext = context;
        this.saveConfirmDialog = true;
    }
    canEditCurrentSave(): boolean {
        const save = this.currentSave as ClassicTreeSave;
        if (!save) {
            return false;
        } else if (save.name === this.defaultName) {
            return true;
        } else if (!save.partner || save.partner.trim() === '') {
            return this.isSuperAdmin;
        } else {
            return true;
        }
    }

    canDeleteCurrentSave(): boolean {
        return this.canEditCurrentSave();
    }

    canAffectPartners(): boolean {
        const save = this.currentSave as ClassicTreeSave;
        if (!save || !save.value) {
            return false;
        } else if (save.partner) {
            return false;
        } else {
            return this.isSuperAdmin;
        }
    }

    public clear(): void {
        this.currentSave = null;
    }

    /** Règles de tag possibles : un vrai preset est chargé, sur une vue qui joint les contacts. */
    canAutomate(): boolean {
        return this.hasActivePreset() && !!this.table?.personalInView;
    }

    refreshTagRules(): void {
        const preset = this.currentSave as ClassicTreeSave;
        const dataviewId = this.table?.dataView?.id;
        if (!this.hasActivePreset() || !preset?.id || !dataviewId || !this.tenant) {
            this.tagRules = [];
            return;
        }
        this.engineTagEventService.getAllRegle(this.tenant, dataviewId, preset.id).subscribe(rules => {
            // Réponse d'un preset qu'on a quitté entre-temps : ignorée.
            if ((this.currentSave as ClassicTreeSave)?.id === preset.id) {
                this.tagRules = rules;
                this.cdr.markForCheck();
            }
        });
    }

    /** Sans règle, « Automatiser » crée directement ; sinon la liste s'ouvre, avec « Nouvelle règle ». */
    openTagRules(event: Event): void {
        if (this.tagRules.length === 0) {
            this.automate();
        } else {
            this.tagRulesPanel.toggle(event);
        }
    }

    /**
     * Nouvelle règle de tag sur le preset chargé, depuis la barre ou depuis le message de résultat d'un
     * tag ponctuel (`initial` reprend alors ses tags et son action).
     */
    automate(initial?: { status: boolean; tagIds: number[] }): void {
        if (!this.canAutomate()) {
            return;
        }
        this.tagRulesPanel?.hide();
        const preset = this.currentSave as ClassicTreeSave;
        this.table.openTagResults({
            rule: {
                presetId: preset.id,
                presetName: preset.name,
                presetModified: this.presetQuerySnapshot != null && this.querySnapshot() !== this.presetQuerySnapshot
            },
            initialStatus: initial?.status,
            initialTagIds: initial?.tagIds
        }).subscribe(result => {
            if (result) {
                this.refreshTagRules();
            }
        });
    }

    openTagRule(rule: TagEventDTO): void {
        this.tagRulesPanel?.hide();
        this.router.navigate(['/h/tag/event', this.tenant, rule.id]);
    }

    tagRuleSchedule(rule: TagEventDTO): string {
        return rule.enableSchedule && rule.cron
            ? cronLabel(rule.cron)
            : this.translate.instant('components.filter-preset.tagRuleOnDemand');
    }

    /** Ce qui distingue deux écrans pour une règle : les filtres et les conditions de comptage, pas le tri. */
    private querySnapshot(): string {
        const filter = this.currentQuery?.filter;
        return JSON.stringify({constraint: filter?.constraint ?? null, having: filter?.havingCountTable ?? []});
    }

    public copyToTenant(): void {
        this.copyToTenantDialog = true;
    }

    public getCurrentSaveQuery(): SaveQuery {
        const currentClassicSave = this.currentSave as ClassicTreeSave;
        if (!currentClassicSave?.id) {
            return null;
        }
        return this.service.list.find(s => s.id === currentClassicSave.id);
    }

    public handleCopySuccess(newPreset: SaveQuery): void {
        this.copyToTenantDialog = false;

        // Si le preset a été copié vers le même tenant/vue, recharger la liste
        if (newPreset.datastore === this._currentView?.datastore &&
            newPreset.view === this._currentView?.view) {
            this.service.loadAll(this._currentView, newPreset);
        }
    }

}
